import AsyncStorage from '@react-native-async-storage/async-storage';
import type { MediaAsset } from '-/Custom/Util.types';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

type ChatDraft = {
  text: string;
  html?: string;
  editedMessageId?: string | null;
};

type ChatStoreState = {
  viewedChatId: string | null;
  chatInput: Record<string, ChatDraft | undefined>;
  cursor: Map<string, string>;
  chatMedia: Map<string, Set<MediaAsset>>;
  uploadMediaMeta: Map<string, { isUploading: boolean }>;
};

const initialState: ChatStoreState = {
  viewedChatId: null,
  chatInput: {},
  cursor: new Map(),
  chatMedia: new Map(),
  uploadMediaMeta: new Map(),
};

const useChatStore = createWithEqualityFn<ChatStoreState>()(
  persist(() => initialState, {
    name: 'useChatStore',
    storage: createJSONStorage(() => AsyncStorage),
    partialize: (state) => ({ chatInput: state.chatInput }),
  }),
);

export function resetChatSession() {
  useChatStore.setState(initialState);
  if (!useChatStore.persist.hasHydrated()) {
    const unsubscribe = useChatStore.persist.onFinishHydration(() => {
      unsubscribe();
      useChatStore.setState(initialState);
    });
  }
}

export function addChatMedia(chatId: string, asset: MediaAsset) {
  useChatStore.setState((state) => {
    const chatMediaMap = new Map(state.chatMedia);
    const previousChatMediaSet = new Set(chatMediaMap.get(chatId));
    const newData = [...Array.from(previousChatMediaSet), asset];
    const chatMediaSet = new Set(newData);
    chatMediaMap.set(chatId, chatMediaSet);

    return {
      chatMedia: chatMediaMap,
    };
  });
}

export function clearChatInput(id: string) {
  useChatStore.setState((state) => {
    const drafts = { ...state.chatInput };
    delete drafts[id];
    return { chatInput: drafts };
  });
}

export function clearChatMedia(chatId: string) {
  useChatStore.setState((state) => {
    const chatMediaMap = new Map(state.chatMedia);
    chatMediaMap.delete(chatId);

    return {
      chatMedia: chatMediaMap,
    };
  });
}

export function removeChatMedia(chatId: string, asset: MediaAsset) {
  useChatStore.setState((state) => {
    const chatMediaMap = new Map(state.chatMedia);
    const chatMediaSet = new Set(chatMediaMap.get(chatId));
    chatMediaSet.delete(asset);
    chatMediaMap.set(chatId, chatMediaSet);

    return {
      chatMedia: chatMediaMap,
    };
  });
}

export function removeUploadMediaMeta(id: string) {
  useChatStore.setState((state) => {
    const chatMediaMap = new Map(state.uploadMediaMeta);
    chatMediaMap.delete(id);

    return {
      uploadMediaMeta: chatMediaMap,
    };
  });
}

export function setChatInput(id: string, value: Partial<ChatDraft>) {
  useChatStore.setState((state) => ({
    chatInput: {
      ...state.chatInput,
      [id]: { text: '', ...state.chatInput[id], ...value },
    },
  }));
}

export function setCursor(id: string, value: string) {
  useChatStore.setState((state) => {
    const map = new Map(state.cursor);
    map.set(id, value);

    return {
      cursor: map,
    };
  });
}

export function setUploadMediaMeta(
  id: string,
  media: { isUploading: boolean },
) {
  useChatStore.setState((state) => {
    const chatMediaMap = new Map(state.uploadMediaMeta);
    chatMediaMap.set(id, media);

    return {
      uploadMediaMeta: chatMediaMap,
    };
  });
}

export { useChatStore };
